import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function BuchenPage() {
  const { t } = useTranslation();

  useEffect(() => {
    const interval = setInterval(() => {
      if ((window as any).dirs21) {
        try {
          (window as any).dirs21.render();
          (window as any).dirs21.rebindTriggers();
        } catch (e) {}
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const notes = [
    { icon: 'ri-login-box-line',  label: t('bookNote1') },
    { icon: 'ri-logout-box-line', label: t('bookNote2') },
    { icon: 'ri-restaurant-line', label: t('bookNote3') },
    { icon: 'ri-car-line',        label: t('bookNote4') },
    { icon: 'ri-wifi-line',       label: t('bookNote5') },
    { icon: 'ri-forbid-line',     label: t('bookNote6') },
  ];

  const rooms = [
    { id: 'doppelzimmer',         icon: 'ri-hotel-bed-line',  name: t('bookRoom1Name'), details: t('bookRoom1Details'), price: t('bookRoom1Price') },
    { id: 'einzelzimmer',         icon: 'ri-home-4-line',     name: t('bookRoom2Name'), details: t('bookRoom2Details'), price: t('bookRoom2Price') },
    { id: 'komfort-doppelzimmer', icon: 'ri-vip-crown-line',  name: t('bookRoom3Name'), details: t('bookRoom3Details'), price: t('bookRoom3Price') },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <section className="relative h-[75vh] sm:h-[80vh] sm:min-h-[640px] flex items-end sm:items-center justify-center overflow-hidden pb-12 sm:pb-0">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/66e3a71d375813cf269344c532b8284d.jpeg"
            alt="Zimmer buchen im Hotel Alpenhof Gauting nahe Starnberger See"
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10" />
        </div>
        <div className="relative z-10 w-full px-4 sm:px-6 text-left sm:pt-44">
          <div className="max-w-5xl mx-auto text-white">
            <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase mb-3 sm:mb-4 text-amber-300">{t('bookHeroLabel')}</p>
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-serif leading-snug sm:leading-tight mb-3 sm:mb-6">
              {t('bookHeroTitle').split('\n').map((line, i, arr) => (
                <span key={i} className={i === arr.length - 1 ? 'text-amber-200' : ''}>{line}<br /></span>
              ))}
            </h1>
            <p className="text-sm sm:text-lg text-white/80 max-w-xl leading-relaxed">{t('bookHeroText')}</p>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-5xl mx-auto space-y-8">

          <div className="w-full">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100">
              <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-6 py-5 flex items-center gap-3 rounded-t-2xl">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20">
                  <i className="ri-calendar-2-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-base">{t('bookSystemTitle')}</p>
                  <p className="text-white/70 text-xs">{t('bookSystemSub')}</p>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <div id="dirs21-widget" className="flex justify-center">
                  <div className="d21-quickbook w-full"></div>
                </div>
                <div className="mt-6 pt-6 border-t border-stone-100 flex items-center gap-2 text-sm text-stone-500">
                  <i className="ri-phone-line text-amber-600"></i>
                  <span>{t('bookPhoneLabel')} <a href="tel:+498985642910" className="text-amber-700 font-medium hover:underline">+49 (0) 89 85 64 29 10</a></span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
              <h3 className="text-sm font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <i className="ri-information-line text-amber-600"></i>
                {t('bookNotesTitle')}
              </h3>
              <ul className="space-y-3">
                {notes.map(({ icon, label }) => (
                  <li key={label} className="flex items-center gap-3 text-sm text-stone-600">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-50 flex-shrink-0">
                      <i className={`${icon} text-amber-600 text-sm`}></i>
                    </div>
                    {label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
              <h3 className="text-sm font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <i className="ri-hotel-bed-line text-amber-600"></i>
                {t('bookRoomsTitle')}
              </h3>
              <div className="space-y-3">
                {rooms.map((room) => (
                  <div key={room.id} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${room.icon} text-amber-600 text-lg`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-stone-800 leading-tight">{room.name}</p>
                      <p className="text-xs text-stone-500">{room.details}</p>
                    </div>
                    <span className="text-xs font-bold text-amber-700 whitespace-nowrap">{room.price}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-600 to-amber-700 rounded-2xl p-6 text-white">
              <h3 className="font-semibold text-sm mb-1">{t('bookAdviceTitle')}</h3>
              <p className="text-white/80 text-xs mb-4 leading-relaxed">{t('bookAdviceText')}</p>
              <a href="tel:+498985642910" className="flex items-center gap-2 bg-white/20 hover:bg-white/30 transition-colors rounded-xl px-4 py-3 text-sm font-medium whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line text-base"></i>
                +49 (0) 89 85 64 29 10
              </a>
              <a href="mailto:hotel@alpenhof-gauting.de" className="mt-2 flex items-center gap-2 bg-white/20 hover:bg-white/30 transition-colors rounded-xl px-4 py-3 text-sm font-medium whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line text-base"></i>
                hotel@alpenhof-gauting.de
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
